// src/components/Layout/Footer.tsx
// Footer-komponent för SireusCode

import React from 'react';
import { Mail, Phone, MapPin, ArrowUp, Code, Heart, Linkedin, Github, Twitter } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
  const { state } = useAppContext();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    services: [
      { 
        label: state.language === 'sv' ? 'Systemutveckling' : 'System Development', 
        href: '#services' 
      },
      { 
        label: state.language === 'sv' ? 'Molntjänster' : 'Cloud Services', 
        href: '#services' 
      },
      { 
        label: state.language === 'sv' ? 'AI-lösningar' : 'AI Solutions', 
        href: '#services' 
      },
      { 
        label: state.language === 'sv' ? 'Systemintegration' : 'System Integration', 
        href: '#services' 
      }
    ],
    company: [
      { 
        label: state.language === 'sv' ? 'Om oss' : 'About us', 
        href: '#about' 
      },
      { 
        label: state.language === 'sv' ? 'Vår process' : 'Our process', 
        href: '#process' 
      },
      { 
        label: state.language === 'sv' ? 'Karriär' : 'Careers', 
        href: '#' 
      },
      { 
        label: state.language === 'sv' ? 'Blogg' : 'Blog', 
        href: '#' 
      }
    ],
    legal: [
      { 
        label: state.language === 'sv' ? 'Integritetspolicy' : 'Privacy Policy', 
        href: '#' 
      },
      { 
        label: state.language === 'sv' ? 'Användarvillkor' : 'Terms of Service', 
        href: '#' 
      },
      { 
        label: state.language === 'sv' ? 'GDPR' : 'GDPR', 
        href: '#' 
      },
      { 
        label: state.language === 'sv' ? 'Cookies' : 'Cookies', 
        href: '#' 
      }
    ]
  };

  const socialLinks = [
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/in/gunnar-sir%C3%A9us-6203872/',
      icon: Linkedin,
      color: 'hover:text-blue-600'
    },
    {
      name: 'GitHub',
      href: 'https://github.com/orgs/developersplaybook/repositories',
      icon: Github,
      color: 'hover:text-gray-900'
    },
    {
      name: 'Twitter',
      href: 'https://twitter.com/sireuscode',
      icon: Twitter,
      color: 'hover:text-blue-400'
    }
  ];

  const technologies = [
    'React', 'TypeScript', '.NET Core', 'Azure', 'AWS', 'Docker', 'PostgreSQL', 'Node.js'
  ];

  return (
    <footer className={`bg-gray-900 text-white ${className}`}>
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Company info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Code className="w-6 h-6 text-blue-400" />
                Sireus Consulting AB
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {state.language === 'sv' 
                  ? 'Vi löser dina IT-utmaningar med skräddarsydda lösningar. 25+ års erfarenhet inom systemutveckling.'
                  : 'We solve your IT challenges with tailored solutions. 25+ years of experience in system development.'
                }
              </p>
              
              {/* Contact info */}
              <div className="space-y-3">
                <a 
                  href="tel:0708 - 940 567"
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4 text-green-400" />
                  0708 - 940 567
                </a>
                <a 
                  href="mailto:gunnar@sireus.se"
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4 text-blue-400" />
                  gunnar@sireus.se
                </a>
                <div className="flex items-center gap-3 text-gray-400">
                  <MapPin className="w-4 h-4 text-purple-400" />
                  Stockholm, Sverige
                </div>
              </div>
            </div>
            
            {/* Social links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-2 rounded-lg bg-gray-800 text-gray-400 ${social.color} transition-all duration-200 hover:bg-gray-700 hover:scale-110`}
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">
              {state.language === 'sv' ? 'Tjänster' : 'Services'}
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 block py-1"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Company */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">
              {state.language === 'sv' ? 'Företag' : 'Company'}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 block py-1"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Technologies & Legal */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">
              {state.language === 'sv' ? 'Teknologier' : 'Technologies'}
            </h4>
            <div className="flex flex-wrap gap-2 mb-8">
              {technologies.map((tech, index) => (
                <span
                  key={index}
                  className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm hover:bg-gray-700 transition-colors cursor-default"
                >
                  {tech}
                </span>
              ))}
            </div>
            
            <h4 className="text-lg font-semibold text-white mb-6">
              {state.language === 'sv' ? 'Juridiskt' : 'Legal'}
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 block py-1 text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="text-gray-400 text-sm flex items-center gap-2">
              <span>© 2025 Sireus Consulting AB.</span>
              <span className="hidden sm:inline">
                {state.language === 'sv' ? 'Alla rättigheter förbehållna.' : 'All rights reserved.'}
              </span>
              <span className="flex items-center gap-1">
                {state.language === 'sv' ? 'Byggt med' : 'Built with'} 
                <Heart className="w-4 h-4 text-red-400 fill-current" /> 
                {state.language === 'sv' ? 'i Stockholm' : 'in Stockholm'}
              </span>
            </div>
            
            {/* Back to top */}
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg"
            >
              <ArrowUp className="w-4 h-4" />
              {state.language === 'sv' ? 'Tillbaka till toppen' : 'Back to top'}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;